const BASE_URL = import.meta.env.VITE_API_BASE;
if (!BASE_URL) throw new Error("VITE_API_BASE required in client stage env");

type RequestInterceptor = (config: FetchRequestConfig) => Promise<FetchRequestConfig> | FetchRequestConfig;

export interface FetchRequestConfig {
  url?: string;
  method?: string;
  headers: Record<string, string>;
  data?: unknown;
}

interface FetchResponse<T = any> {
  data: T;
  status: number;
  headers: Headers;
}

class FetchClient {
  private requestInterceptors: { id: number; fn: RequestInterceptor }[] = [];
  private nextId = 0;

  interceptors = {
    request: {
      use: (fn: RequestInterceptor): number => {
        const id = this.nextId++;
        this.requestInterceptors.push({ id, fn });
        return id;
      },
      eject: (id: number) => {
        this.requestInterceptors = this.requestInterceptors.filter(i => i.id !== id);
      },
    },
  };

  private async runInterceptors(config: FetchRequestConfig): Promise<FetchRequestConfig> {
    let cfg = config;
    for (const interceptor of this.requestInterceptors) {
      cfg = await interceptor.fn(cfg);
    }
    return cfg;
  }

  async request<T = any>(urlOrConfig: string | FetchRequestConfig, init?: { method?: string; data?: unknown; headers?: Record<string, string> }): Promise<FetchResponse<T>> {
    let config: FetchRequestConfig;

    if (typeof urlOrConfig === 'string') {
      config = {
        url: urlOrConfig,
        method: init?.method || 'GET',
        headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
        data: init?.data,
      };
    } else {
      config = {
        url: urlOrConfig.url,
        method: urlOrConfig.method || 'GET',
        headers: { 'Content-Type': 'application/json', ...(urlOrConfig.headers || {}) },
        data: urlOrConfig.data,
      };
    }

    config = await this.runInterceptors(config);

    const url = config.url?.startsWith('http') ? config.url : `${BASE_URL}${config.url}`;
    const fetchInit: RequestInit = {
      method: config.method,
      headers: config.headers,
    };
    if (config.data !== undefined && config.method !== 'GET') {
      fetchInit.body = JSON.stringify(config.data);
    }

    const res = await fetch(url, fetchInit);
    let data: any;
    const contentType = res.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      data = await res.json();
    } else {
      data = await res.text();
    }

    if (!res.ok) {
      const error: any = new Error(data?.message || data?.error || `Request failed with status ${res.status}`);
      error.response = { status: res.status, data, headers: res.headers };
      throw error;
    }

    return { data, status: res.status, headers: res.headers };
  }

  async get<T = any>(url: string, options?: { headers?: Record<string, string> }): Promise<FetchResponse<T>> {
    return this.request<T>(url, { method: 'GET', headers: options?.headers });
  }

  async post<T = any>(url: string, data?: unknown, options?: { headers?: Record<string, string> }): Promise<FetchResponse<T>> {
    return this.request<T>(url, { method: 'POST', data, headers: options?.headers });
  }
}

export const fetchClient = new FetchClient();
export default fetchClient;
